/**
 * The data-reference picker — ADR-127 D9-D15.
 *
 * A cascading selector: set → member (or runtime operand) → field, then Insert. It offers
 * ONLY what the canonical generator served, and it inserts the token the generator built.
 *
 * WHY IT CONSTRUCTS NOTHING
 * -------------------------
 * D15: "any reference the picker can construct MUST pass validation against the schema it
 * was built from; a failure is a defect in the picker, never operator error." The cheapest
 * way to honour that is to construct nothing at all — every option below is an element of
 * the served offer list, and Insert emits `offer.token` verbatim. There is no code path in
 * this component that assembles a reference from parts, so there is no path by which it can
 * assemble an invalid one.
 *
 * WHAT THE HOVER SHOWS (D11)
 * --------------------------
 * The pinned SCHEMA version and a real sample value. Both come from the endpoint; the
 * sample is `null` when no record carries the field, and that is rendered as "no sample"
 * rather than as an empty string — a blank would read as a value.
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type {
  DataReferenceMatchCandidate, DataReferenceOffer, DataReferenceOffers, DataReferenceSet,
} from '../api/client'

export interface DataReferencePickerProps {
  /** The DSL slot being edited — decides which offers exist AND which syntax they use. */
  slot: string
  /** In-scope runtime operands for a `by_key[...]` index. For an activity these come from
   *  the positional scope (`validate-activities.py --scope`), which is why the caller
   *  passes them rather than the picker guessing. */
  operands?: string[]
  /**
   * The chosen token AND the pin it needs.
   *
   * A `{{data.<alias>.…}}` with no `data:` pin is a validation error (ADR-125 D6),
   * so a picker that returned only the token handed back a reference that could not
   * validate — ADR-127 D15 makes that the picker's responsibility, not the
   * operator's. The picker already knows both facts; it now says both.
   */
  onInsert: (token: string, pin: { alias: string; set: string }) => void
  onCancel?: () => void
  /**
   * Candidates mode — render THESE and fetch nothing.
   *
   * A match candidate already carries everything an offer carries plus the resolved `value`,
   * the `set` it came from, its `schema_version` and its own `pin`, because the matcher had to
   * know all of that to decide the value was equal. Re-asking the offers endpoint to rebuild
   * the same cascade would serve every reference in the fleet and make the operator find the
   * one they were just shown — and it would open a window in which the two lists disagree.
   *
   * The cascade, the hover and the Insert button are the same code as the browse mode: the
   * candidates are projected into the offer shape and nothing below this line knows which
   * mode it is in. So a token here is still selected, never assembled (ADR-127 D15).
   */
  candidates?: DataReferenceMatchCandidate[]
  /**
   * What the confirming button says. Defaults to "Insert reference"; a host that opened the
   * picker to REPLACE a literal (the ADR-179 hint) names that literal here, because the
   * default describes an action the operator is not taking.
   */
  insertLabel?: string
}

/** The member step of the cascade: a literal key, or a runtime operand. Derived from the
 *  offer's PATH rather than parsed out of the token, so the two cannot disagree. */
function memberOf(offer: { path: string }): string {
  if (offer.path === 'items') return '(the whole collection)'
  const bracket = offer.path.match(/^by_key\[([^\]]+)\]/)
  if (bracket) return `${bracket[1]} (runtime)`
  if (offer.path.startsWith('by_key.')) return offer.path.split('.')[1]
  return '(set)'
}

function fieldOf(offer: { path: string }): string {
  if (offer.path === 'items') return 'items'
  const withoutBracket = offer.path.replace(/^by_key\[[^\]]+\]\.?/, '')
  if (withoutBracket !== offer.path) return withoutBracket || 'items'
  if (offer.path.startsWith('by_key.')) return offer.path.split('.').slice(2).join('.')
  return offer.path
}

export function renderSample(sample: DataReferenceOffer['sample']): string {
  // A blank would read as a value. Say there is none.
  if (sample === null || sample === undefined) return 'no sample in this set'
  // Quoted, so a leading or trailing space is visible: 'CHECK - ' shown bare reads as
  // 'CHECK -', and the hint row that opens this picker shows the same value quoted.
  if (typeof sample === 'string') {
    return `"${sample.length > 80 ? `${sample.slice(0, 77)}…` : sample}"`
  }
  return String(sample)
}

/** Project the candidates into the browse mode's set-of-offers shape, so one render path
 *  serves both. `sample` is the resolved value the matcher compared — which is exactly what
 *  ADR-127 D11's hover wants to show, and here it is the value the field already holds. */
function setsFromCandidates(candidates: DataReferenceMatchCandidate[]): DataReferenceSet[] {
  const bySet = new Map<string, DataReferenceSet>()
  for (const c of candidates) {
    let entry = bySet.get(c.set)
    if (!entry) {
      entry = {
        set: c.set, schema: c.schema_version, suggested_alias: c.pin.alias,
        keys: [], keys_truncated: false, key_limit: 0,
        // Browse mode reads this off the set entry; here the same fact arrives as an
        // ADR-127 D14 marker on the candidate, and dropping it on the floor would make a
        // deprecated set look current in exactly the mode that recommends it.
        deprecated: c.notes.includes('deprecated_set'),
        offers: [],
      }
      bySet.set(c.set, entry)
    }
    entry.offers.push({
      token: c.token, chip: c.chip, path: c.path, slot: c.slot, sample: c.value,
      notes: c.notes,
    })
  }
  return Array.from(bySet.values())
}

/** The ADR-127 D14 markers in the operator's words. An unknown marker is shown verbatim
 *  rather than swallowed: a note nobody renders is a note nobody acts on. */
export function renderNote(note: string): string {
  if (note === 'deprecated_field') return 'this field is deprecated'
  if (note === 'deprecated_set') return 'this set is deprecated'
  return note
}

export function DataReferencePicker(props: DataReferencePickerProps) {
  const {
    slot, operands = [], onInsert, onCancel, candidates, insertLabel = 'Insert reference',
  } = props
  const [data, setData] = useState<DataReferenceOffers | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [setRef, setSetRef] = useState('')
  const [member, setMember] = useState('')
  const [field, setField] = useState('')

  useEffect(() => {
    // Candidates mode asks nothing: the list was already computed against this draft.
    if (candidates) return
    let live = true
    setData(null)
    setError(null)
    api.getDataReferenceOffers(slot, operands)
      .then((d) => { if (live) setData(d) })
      .catch((e: Error) => { if (live) setError(e.message || 'could not load offers') })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, operands.join('|')])

  const candidateSets = useMemo(
    () => (candidates ? setsFromCandidates(candidates) : null), [candidates])

  // One candidate is no choice at all, so make it. Several ARE a choice, and the platform
  // does not make it for the operator (ADR-179): all of them are listed, none preselected.
  useEffect(() => {
    if (!candidates || candidates.length !== 1) return
    const only = candidates[0]
    setSetRef(only.set)
    setMember(memberOf(only))
    setField(fieldOf(only))
  }, [candidates])

  const sets: DataReferenceSet[] = candidateSets ?? data?.sets ?? []
  const active = sets.find((s) => s.set === setRef) ?? null

  const members = useMemo(() => {
    if (!active) return []
    return Array.from(new Set(active.offers.map(memberOf)))
  }, [active])

  const fields = useMemo(() => {
    if (!active || !member) return []
    return active.offers.filter((o) => memberOf(o) === member)
  }, [active, member])

  const chosen = fields.find((o) => fieldOf(o) === field) ?? null

  if (error) {
    return (
      <div data-testid="picker-error" role="alert">
        <p>Could not load data references: {error}</p>
        {onCancel && <button onClick={onCancel}>Close</button>}
      </div>
    )
  }
  // No candidates handed in means the picker is browsing the fleet's whole offer list, which
  // is the only mode that waits on the endpoint or can report it unavailable.
  const browsing = !candidateSets
  if (browsing && !data) {
    return <div data-testid="picker-loading">Loading data references…</div>
  }

  // `note` means the generator could not be asked. That is a different fact from "this
  // fleet has no reference data", and saying the wrong one would send an operator looking
  // for a set that exists.
  if (browsing && data?.note) {
    return (
      <div data-testid="picker-unavailable" role="status">
        <p>Data references are unavailable right now: {data.note}</p>
        <p>Nothing was found because nothing could be asked — not because this fleet has none.</p>
        {onCancel && <button onClick={onCancel}>Close</button>}
      </div>
    )
  }
  if (sets.length === 0) {
    return (
      <div data-testid="picker-empty" role="status">
        <p>This fleet has no published reference data yet.</p>
        {onCancel && <button onClick={onCancel}>Close</button>}
      </div>
    )
  }

  return (
    <div data-testid="data-reference-picker">
      <label className="block">
        Data set
        <select
          data-testid="picker-set"
          value={setRef}
          onChange={(e) => { setSetRef(e.target.value); setMember(''); setField('') }}
        >
          <option value="">Choose a set…</option>
          {sets.map((s) => (
            <option key={s.set} value={s.set}>
              {s.set}{s.deprecated ? ' (deprecated)' : ''}
            </option>
          ))}
        </select>
      </label>

      {active && (
        <>
          {/* D11 — the pinned version, on the set the operator just chose. */}
          <p data-testid="picker-pinned">Pinned schema: {active.schema}</p>
          {active.keys_truncated && (
            <p data-testid="picker-truncated" role="status">
              Showing the first {active.key_limit} keys of more — narrow the set or pick a
              runtime operand instead of a literal key.
            </p>
          )}
          <label className="block">
            Member
            <select
              data-testid="picker-member"
              value={member}
              onChange={(e) => { setMember(e.target.value); setField('') }}
            >
              <option value="">Choose a member…</option>
              {members.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
        </>
      )}

      {member && (
        <label className="block">
          Field
          <select
            data-testid="picker-field"
            value={field}
            onChange={(e) => setField(e.target.value)}
          >
            <option value="">Choose a field…</option>
            {fields.map((o) => (
              <option key={o.token} value={fieldOf(o)}>{fieldOf(o)}</option>
            ))}
          </select>
        </label>
      )}

      {chosen && (
        <div data-testid="picker-preview">
          {/* The chip the operator will see in prose, and the token that is stored. Both
              shown, because D13's round-trip is only reassuring if you can see both ends. */}
          <p data-testid="picker-chip">{chosen.chip}</p>
          <code data-testid="picker-token" className="block break-all">{chosen.token}</code>
          <p data-testid="picker-sample">Sample: {renderSample(chosen.sample)}</p>
          {(chosen.notes ?? []).length > 0 && (
            <p data-testid="picker-notes">
              {(chosen.notes ?? []).map(renderNote).join('; ')}
            </p>
          )}
        </div>
      )}

      <div data-testid="picker-actions" className="mt-2 flex items-center gap-3">
        <button
          data-testid="picker-insert"
          disabled={!chosen}
          onClick={() => chosen && active && onInsert(chosen.token, {
            alias: active.suggested_alias, set: active.set,
          })}
          className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {insertLabel}
        </button>
        {onCancel && (
          <button
            data-testid="picker-cancel"
            onClick={onCancel}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
